import { useState, useMemo } from 'react'
import dayjs from 'dayjs'
import {
  buildPaymentCycleOption,
  formatPaymentCycleLabel,
  getRangeKey,
  isCalendarMonthRange,
  isPaymentCycleCalendarMonth,
} from '../utils/paymentCycleDateRanges'

function getInitialMode(initialStartDate, initialEndDate, paymentConfig, periodOptions) {
  if (isCalendarMonthRange(initialStartDate, initialEndDate) && isPaymentCycleCalendarMonth(paymentConfig)) {
    return 'monthly'
  }

  const selectedKey = getRangeKey(initialStartDate, initialEndDate)
  const hasMatchingPeriod = (periodOptions || []).some((option) => option.key === selectedKey)
  return hasMatchingPeriod || !isPaymentCycleCalendarMonth(paymentConfig) ? 'period' : 'custom'
}

export const useDateRangeLogic = (initialStartDate, initialEndDate, options = {}) => {
  const { paymentConfig, periodOptions = [] } = options
  const selectedPeriodOption = buildPaymentCycleOption(
    initialStartDate,
    initialEndDate,
    formatPaymentCycleLabel(initialStartDate, initialEndDate) || 'Selected range',
  )
  const [dateRangeMode, setDateRangeMode] = useState(() =>
    getInitialMode(initialStartDate, initialEndDate, paymentConfig, periodOptions),
  )
  const [dialogStartDate, setDialogStartDate] = useState(dayjs(initialStartDate))
  const [customDateRange, setCustomDateRange] = useState([
    dayjs(initialStartDate),
    dayjs(initialEndDate)
  ])
  const [selectedPeriodRange, setSelectedPeriodRange] = useState(selectedPeriodOption)

  const getReportDateRange = useMemo(() => {
    if (dateRangeMode === 'monthly') {
      return {
        start: dialogStartDate.clone().startOf('month'),
        end: dialogStartDate.clone().endOf('month')
      }
    }
    if (dateRangeMode === 'period') {
      return {
        start: dayjs(selectedPeriodRange.start).startOf('day'),
        end: dayjs(selectedPeriodRange.end).endOf('day')
      }
    } else {
      return {
        start: dayjs(customDateRange[0]).startOf('day'),
        end: dayjs(customDateRange[1]).endOf('day')
      }
    }
  }, [dateRangeMode, dialogStartDate, customDateRange, selectedPeriodRange])

  const isValidDateRange = useMemo(() => {
    if (dateRangeMode === 'monthly') {
      return dialogStartDate && dialogStartDate.isValid()
    }
    if (dateRangeMode === 'period') {
      return selectedPeriodRange?.start && selectedPeriodRange?.end
    }
    return (
      customDateRange &&
      customDateRange[0] &&
      customDateRange[1] &&
      !dayjs(customDateRange[1]).isBefore(dayjs(customDateRange[0]), 'day')
    )
  }, [dateRangeMode, dialogStartDate, customDateRange, selectedPeriodRange])

  const generateFileName = (fileNamePrefix) => {
    if (dateRangeMode === 'monthly') {
      const formattedStartDate = dialogStartDate.clone().startOf('month').format('MMM-YYYY')
      return `${fileNamePrefix}_${formattedStartDate}.xlsx`
    } else if (dateRangeMode === 'period') {
      const formattedPeriod = formatPaymentCycleLabel(selectedPeriodRange.start, selectedPeriodRange.end)
      return `${fileNamePrefix}_${formattedPeriod}.xlsx`
    } else {
      const formattedStartDate = dayjs(customDateRange[0]).format('DD-MMM-YYYY')
      const formattedEndDate = dayjs(customDateRange[1]).format('DD-MMM-YYYY')
      return `${fileNamePrefix}_${formattedStartDate}_to_${formattedEndDate}.xlsx`
    }
  }

  return {
    dateRangeMode,
    setDateRangeMode,
    dialogStartDate,
    setDialogStartDate,
    customDateRange,
    setCustomDateRange,
    selectedPeriodRange,
    setSelectedPeriodRange,
    getReportDateRange,
    isValidDateRange,
    generateFileName
  }
}
